import Global from "@/assets/icons/Global";
import { Button } from "@/components/shared/Button/Button";
import { Input } from "@/components/shared/Input/Input";
import { Label } from "@/components/shared/Label/Label";
import Title from "@/components/shared/Title/Title";
import LayoutOutside from "@/layout/LayoutOutside";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { LoginTwoFactorStep } from "./components/LoginTwoFactorStep";
import { useLogin } from "./hooks/useLogin";

export function Login() {
  const {
    step,
    onSubmit,
    handleSubmit,
    register,
    errors,
    isLoading,
    isVerifyingTwoFactor,
    isResendingTwoFactor,
    isError,
    isSuccess,
    inviteEmail,
    twoFactorEmail,
    twoFactorError,
    resendCooldownSec,
    verifyTwoFactor,
    resendTwoFactor,
    handleBackToCredentials,
  } = useLogin();
  const { t } = useTranslation("common");

  const isEmailLocked = !!inviteEmail;
  const isTwoFactorStep = step === "two_factor";

  return (
    <LayoutOutside
      isLoading={isLoading || isVerifyingTwoFactor}
      isError={isError}
      isSuccess={isSuccess}
      route="/register"
      text={t("signup")}
      classNameMainContainer="flex items-center justify-center h-[calc(100vh-80px)]"
    >
      {isTwoFactorStep ? (
        <LoginTwoFactorStep
          email={twoFactorEmail}
          isLoading={isVerifyingTwoFactor}
          isResending={isResendingTwoFactor}
          resendCooldownSec={resendCooldownSec}
          error={twoFactorError}
          onVerify={(code) => verifyTwoFactor(code)}
          onResend={() => resendTwoFactor()}
          onBack={handleBackToCredentials}
        />
      ) : (
        <form
          className="space-y-8 rounded-lg max-w-[416px] w-full"
          onSubmit={handleSubmit(onSubmit)}
          noValidate
        >
          <header>
            <Global />
            <Title className="mt-3" text={t("login")} />
          </header>

          <div className="space-y-4 w-full">
            <Input
              label={t("email")}
              type="email"
              placeholder={t("email_placeholder")}
              helperText={errors?.email?.message}
              disabled={isLoading || isEmailLocked}
              data-cy="email"
              {...register("email")}
            />

            <div className="space-y-1.5">
              <div className="flex justify-between items-center gap-1">
                <Label htmlFor="password">{t("password")}</Label>
                <Link
                  to="/forgot-password"
                  className="text-interactive text-sm font-medium"
                >
                  {t("recover_password")}
                </Link>
              </div>
              <Input
                type="password"
                placeholder={t("password_placeholder")}
                helperText={errors?.password?.message}
                disabled={isLoading}
                data-cy="password"
                {...register("password")}
              />
            </div>

            <Button
              variant="primary"
              className="w-full"
              type="submit"
              disabled={isLoading}
              data-cy="submit-button"
            >
              {isLoading ? t("submitting") : t("submit")}
            </Button>
          </div>
        </form>
      )}
    </LayoutOutside>
  );
}
